import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Checkbox, Icon, List, Message, Modal } from "semantic-ui-react";
import {
  addHistoryToDb,
  updateDocAddSharingInDb,
  updateTransAddSharingInDb,
} from "../../../../app/firestore/firestoreService";
import {
  functionCreatePropertyIntelligencePdf,
  functionVerifyPropertyIntelligenceSharing,
} from "../../../../app/firestore/functionsService";
import { fetchEmailFromTemplate } from "../../../emails/emailSlice";
import { closeModal } from "../../../../app/common/modals/modalSlice";
import { NAVY, GREY_TEXT, addressLine, buildClientEmail, portalLoginUrl } from "./piModel";

const STEP_LABELS = {
  pdf: "Create PDF and save to Documents",
  portal: "Share in the client portal",
  email: "Prepare email in the composer",
};

function StepStatus({ step, state }) {
  if (!state) return null;
  const icon = state.status === "done" ? { name: "check circle", color: "green" }
    : state.status === "error" ? { name: "times circle", color: "red" }
      : state.status === "working" ? { name: "circle notched", loading: true }
        : { name: "circle outline", color: "grey" };
  return (
    <List.Item>
      <Icon {...icon} />
      <List.Content>
        <List.Header>{STEP_LABELS[step]}</List.Header>
        {state.message && <List.Description>{state.message}</List.Description>}
      </List.Content>
    </List.Item>
  );
}

/**
 * Share with Client: email, PDF to the client portal, or both. Uses only the
 * saved, approved summary. The PDF is saved through the server into the
 * transaction's Documents; portal visibility uses the existing sharing
 * functions (updateDocAddSharingInDb + updateTransAddSharingInDb) and is
 * verified server-side before any portal link is offered. Email is only
 * prepared in the existing composer - never sent from here.
 */
export default function PiShareModal({ open, onClose, report, propertyKey, transaction, clientParties, initialRecipientIds }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const [wantEmail, setWantEmail] = useState(true);
  const [wantPdf, setWantPdf] = useState(true);
  const [recipientIds, setRecipientIds] = useState(() => (initialRecipientIds?.length ? initialRecipientIds : clientParties.map((p) => p.id)));
  const [steps, setSteps] = useState({});
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const approved = report.clientSummary?.approved;
  const recipients = clientParties.filter((p) => recipientIds.includes(p.id));
  const address = addressLine(report.propertyAddress || transaction.address);

  const setStep = (key, status, message) => setSteps((prev) => ({ ...prev, [key]: { status, message } }));

  async function run() {
    setRunning(true);
    setFinished(false);
    setSteps({
      ...(wantPdf ? { pdf: { status: "pending" }, portal: recipients.length ? { status: "pending" } : undefined } : {}),
      ...(wantEmail ? { email: { status: "pending" } } : {}),
    });
    let portalVerified = false;
    if (wantPdf) {
      let docId = null;
      let docName = null;
      setStep("pdf", "working");
      try {
        const res = await functionCreatePropertyIntelligencePdf({
          transactionId: transaction.id,
          propertyKey,
          preparedFor: recipients.map((r) => `${r.firstName || ""} ${r.lastName || ""}`.trim()).filter(Boolean).join(" & ") || null,
        });
        docId = res.docId;
        docName = res.filename;
        setStep("pdf", "done", res.existing ? "This approved version was already saved in Documents - no duplicate was created." : `Saved to Documents${res.logoNote ? `. ${res.logoNote}` : "."}`);
      } catch (error) {
        setStep("pdf", "error", error?.message || "The PDF could not be created.");
      }

      if (docId && recipients.length) {
        setStep("portal", "working");
        try {
          for (const party of recipients) {
            await updateDocAddSharingInDb(docId, party, transaction, null, false, null);
            await updateTransAddSharingInDb(transaction.id, party, transaction);
          }
          const check = await functionVerifyPropertyIntelligenceSharing({
            transactionId: transaction.id,
            propertyKey,
            docId,
            recipientEmails: recipients.map((r) => r.email),
          });
          if (check.verified) {
            portalVerified = true;
            for (const party of recipients) {
              await addHistoryToDb(transaction.id, currentUserProfile, "shared", docName || "Property Intelligence Report", party);
            }
            setStep("portal", "done", `Visible to ${recipients.map((r) => r.firstName || r.email).join(", ")} in their client portal.`);
          } else {
            setStep("portal", "error", check.missing?.length
              ? `Sharing didn't reach: ${check.missing.join(", ")}. Try again from Documents.`
              : `The document is shared with someone outside this transaction (${check.outsiders.join(", ")}). Review its sharing in Documents.`);
          }
        } catch (error) {
          setStep("portal", "error", error?.message || "Portal sharing failed.");
        }
      } else if (docId) {
        setStep("portal", "error", "No client recipient selected - the PDF is in Documents but not shared.");
      }
    }
    if (wantEmail) {
      setStep("email", recipients.length ? "pending" : "error", recipients.length
        ? `Ready - opens the email composer for you to review and send.${wantPdf && !portalVerified ? " The portal link was left out because sharing wasn't confirmed." : ""}`
        : "Select at least one client recipient.");
    }
    setRunning(false);
    setFinished(true);
    return portalVerified;
  }

  function openComposer() {
    const portalShared = steps.portal?.status === "done";
    const email = buildClientEmail({
      address,
      summary: approved,
      recipients,
      currentUserProfile,
      portalLink: portalShared ? portalLoginUrl() : null,
      pdfShared: portalShared,
    });
    dispatch(fetchEmailFromTemplate(email));
    onClose();
    dispatch(closeModal({ modalType: "PropertyIntelligenceModal" }));
    navigate(`/transactions/${transaction.id}/emails`);
  }

  return (
    <Modal open={open} onClose={running ? undefined : onClose} size="small" closeIcon={!running}>
      <Modal.Header style={{ color: NAVY }}>Share with Client</Modal.Header>
      <Modal.Content>
        {!approved ? (
          <Message warning>Approve the client summary first (Client Summary tab).</Message>
        ) : (
          <>
            <p style={{ color: GREY_TEXT }}>Shares approved version {approved.version} of the client summary.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
              <Checkbox label="Prepare email to client" checked={wantEmail} disabled={running} onChange={(e, { checked }) => setWantEmail(checked)} />
              <Checkbox label="Create PDF and add to client portal" checked={wantPdf} disabled={running} onChange={(e, { checked }) => setWantPdf(checked)} />
            </div>
            <div style={{ fontWeight: 700, color: NAVY, marginBottom: 6 }}>Client recipients</div>
            {clientParties.length === 0 ? (
              <p style={{ color: GREY_TEXT }}>No client with an email address is on this transaction. The PDF can still be saved to Documents.</p>
            ) : (
              clientParties.map((p) => (
                <div key={p.id} style={{ marginBottom: 4 }}>
                  <Checkbox
                    disabled={running}
                    label={`${p.firstName || ""} ${p.lastName || ""} (${p.role}) - ${p.email}`}
                    checked={recipientIds.includes(p.id)}
                    onChange={() => setRecipientIds((ids) => (ids.includes(p.id) ? ids.filter((x) => x !== p.id) : [...ids, p.id]))}
                  />
                </div>
              ))
            )}
            {Object.keys(steps).length > 0 && (
              <List relaxed style={{ marginTop: 16 }}>
                {Object.entries(steps).filter(([, s]) => s).map(([key, state]) => <StepStatus key={key} step={key} state={state} />)}
              </List>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button basic disabled={running} onClick={onClose} content={finished ? "Close" : "Cancel"} />
        {approved && !finished && (
          <Button primary loading={running} disabled={running || (!wantEmail && !wantPdf)} onClick={run} content="Continue" />
        )}
        {approved && finished && wantEmail && recipients.length > 0 && (
          <Button primary icon="mail" content="Open Email Composer" onClick={openComposer} />
        )}
      </Modal.Actions>
    </Modal>
  );
}
